<script>
import { SuawLogo, SuawDropdown, SuawButton } from "@/components";
import SuawHeaderMobile from "./HeaderMobile.vue";

export default {
  name: "Header",
  components: {
    SuawLogo,
    SuawDropdown,
    SuawButton,
    SuawHeaderMobile
  },
  props: {
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    showAvatar: {
      type: Boolean,
      default: false
    },
    displayName: {
      type: String,
      required: true
    },
    totalNotifications: {
      type: String,
      default: ""
    },
    eventLinks: {
      type: Array,
      default: () => []
    },
    aboutLinks: {
      type: Array,
      default: () => []
    },
    navLinks: {
      type: Array,
      default: () => []
    },
    authenticatedUserLinks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showNavbar: false,
      dropdownDirection: "right"
    };
  },
  computed: {
    iconOpen() {
      return this.showNavbar ? "IconCloseBig" : "";
    },
    iconClose() {
      return !this.showNavbar ? "IconHamburger" : "";
    },
    toggleNavbarClasses() {
      return {
        "show-navbar": this.showNavbar
      };
    }
  },
  mounted() {
    this.handleResize(); // Set initial direction based on current window size
    window.addEventListener("resize", this.handleResize);
  },
  updated() {
    // Apply or remove overflow style based on showNavbar
    // document.body.style.overflow = this.showNavbar ? "hidden" : "auto";
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    // document.body.style.overflow = "auto";
  },
  methods: {
    toggleNavbarVisibility() {
      this.showNavbar = !this.showNavbar;
    },
    handleResize() {
      this.dropdownDirection = window.innerWidth <= 1024 ? "left" : "right";
    },
    onLogInClick() {
      this.$emit("login-click", this.$route.fullPath);
      this.toggleNavbarVisibility();
    },
    onLogOutClick() {
      this.toggleNavbarVisibility();
    }
  }
};
</script>

<template>
  <header :class="['suaw-header']">
    <div class="suaw-header__main-container">
      <a class="suaw-header__logo-link" href="https://www.shutupwrite.com">
        <SuawLogo type="LogoDefault" class="suaw-header__logo" />
      </a>
      <SuawButton
        :key="showNavbar.toString()"
        type="secondary"
        size="medium"
        class="suaw-header__button"
        :icon-left="iconOpen"
        :icon-right="iconClose"
        @click="toggleNavbarVisibility"
      />
      <transition name="fade">
        <SuawHeaderMobile
          v-if="showNavbar"
          class="suaw-header__mobile-navbar"
          :is-authenticated="isAuthenticated"
          :show-avatar="showAvatar"
          :total-notifications="totalNotifications"
          :event-links="eventLinks"
          :about-links="aboutLinks"
          :nav-links="navLinks"
          :authenticated-user-links="authenticatedUserLinks"
          @login-click="onLogInClick"
          @toggle-navbar="showNavbar = false"
        />
      </transition>
      <nav class="suaw-header__navbar">
        <SuawDropdown
          label="About"
          button-class-name="suaw-dropdown__button--header suaw-dropdown__button--no-pd"
          class="suaw-header__dropdown--no-pd suaw-header__dropdown-navlink"
          button-type="secondary-ghost"
          :items="aboutLinks"
          :direction="dropdownDirection"
          @item-click="toggleNavbarVisibility"
        />
        <SuawDropdown
          label="Events"
          button-class-name="suaw-dropdown__button--header suaw-dropdown__button--no-pd"
          class="suaw-header__dropdown--no-pd suaw-header__dropdown-navlink"
          button-type="secondary-ghost"
          :items="eventLinks"
          :direction="dropdownDirection"
          @item-click="toggleNavbarVisibility"
        />
        <a v-for="(link, index) in navLinks" :key="index" :href="link.url" class="suaw-header__link" :target="link.external ? '_blank' : '_self'">
          {{ link.text }}
        </a>
        <a v-if="!isAuthenticated" href="#" class="suaw-header__link" @click.prevent="onLogInClick">
          Log In
        </a>
        <SuawDropdown
          v-if="isAuthenticated"
          :label="displayName"
          :show-avatar="showAvatar"
          button-class-name="suaw-dropdown__button--header suaw-dropdown__button--no-pd"
          class="suaw-header__dropdown--no-pd"
          button-type="secondary-ghost"
          :items="authenticatedUserLinks"
          :total-notifications="totalNotifications"
          :direction="dropdownDirection"
          @item-click="toggleNavbarVisibility"
        />
      </nav>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.suaw-header {
  @media screen and (min-width: 1025px) {
    --comp-header-navbar-visibility: flex;
    --comp-header-navbar-direction: row;
    --comp-header-navbar-alignment: center;
    --comp-header-navbar-width: auto;
    --comp-header-button-visibility: none;
    --comp-header-logo-height: 56px;
    --comp-header-logo-width: 132px;
    --comp-header-navbar-margin-top: 0;
  }

  @media screen and (max-width: 1024px) {
    --comp-header-navbar-visibility: none;
    --comp-header-navbar-direction: column;
    --comp-header-navbar-alignment: flex-start;
    --comp-header-navbar-width: 100%;
    --comp-header-button-visibility: flex;
    --comp-header-logo-height: 34px;
    --comp-header-logo-width: 80px;
    --comp-header-navbar-margin-top: 1rem;
  }

  background-color: var(--sem-color-background-lightest);
  padding: var(--sem-space-triple);
  position: relative;
  width: 100%;
  z-index: 995;

  @media (max-width: 767px) {
    padding: var(--sem-space-triple) var(--sem-space-double); // Override padding for mobile view
  }

  &--default {
    border-bottom: 1px solid var(--sem-color-border-regular);
  }

  &--shadow {
    box-shadow: var(--soft-shadow-draft);
  }

  &__main-container {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: auto;
    max-width: 1120px;
  }

  &__logo-link {
    display: flex;
    transition: opacity, 0.25s linear;
  }

  &__logo-link:hover {
    opacity: 0.75;
  }

  &__logo {
    height: var(--comp-header-logo-height);
    width: var(--comp-header-logo-width);
  }

  &__button {
    display: var(--comp-header-button-visibility);
  }

  &__mobile-navbar {
    display: var(--comp-header-button-visibility);
  }

  &__navbar {
    align-items: var(--comp-header-navbar-alignment);
    flex-direction: var(--comp-header-navbar-direction);
    display: var(--comp-header-navbar-visibility);
    gap: var(--sem-space-triple);
    position: relative;
    width: var(--comp-header-navbar-width);
  }

  &__link {
    color: var(--sem-color-text-regular);
    font-family: var(--button-bold-font-family);
    font-size: var(--button-bold-font-size);
    font-style: var(--button-bold-font-style);
    font-weight: var(--button-bold-font-weight);
    letter-spacing: var(--button-bold-letter-spacing);
    line-height: var(--button-bold-line-height);
    position: relative;
    text-decoration: none;
    transition: color, 0.25s linear;
    white-space: nowrap;
  }

  &__link:hover {
    color: var(--sem-color-text-darkest);
  }

  &__dropdown-navlink:hover {
  }
}
</style>
